import { AppSidebar, NavbarLink, Tooltip } from '@/common'
import { Session } from '@/api/models/UserModel'
import { useLogout } from '@/features/auth/hooks/auth'
import useI18n from '@/shared/hooks/usei18n'
import UserMenu from './UserMenu'
import DeclarantLanguageSelector from './LanguageSelector'
import DeclarantsMenu from './DeclarantsMenu'
import { useAppState } from '@/shared/hooks/useAppState'
import { DeclarantType, useGetAllDeclarants } from '@/features/cbam-reporting/hooks/declarants'
import { useGetOrganization } from '@/features/organization/hooks/organizations'

function NavigationSectionTitle({ title }: { title: string }) {
  return <h2 className="text-gray-500 text-xxs uppercase py-1 border-b border-gray-400">{title}</h2>
}

export default function Navbar({ session }: { session: Session }) {
  const { mutateAsync: logout } = useLogout()
  const { t } = useI18n()
  const { selectedDeclarantId } = useAppState()
  const { data: declarants = [] } = useGetAllDeclarants(session.organization.id)
  const { data: organization } = useGetOrganization(session.organization.id)
  const activeDeclarantId = selectedDeclarantId || declarants[0]?.id

  const declarantOptions = declarants.map(declarant => ({
    id: declarant.id,
    name: declarant.name,
    isActive: declarant.id === activeDeclarantId
  }))

  const declarantName = declarantOptions.find(declarant => declarant.id === activeDeclarantId)?.name
  const hasRepresentativeDeclarant = declarants.some(
    declarant => declarant.type === DeclarantType.REPRESENTATIVE
  )
  return (
    <AppSidebar
      appLabel="Declarant"
      acountSection={
        session && (
          <div className="space-y-1">
            <NavigationSectionTitle title={t('common:navbar.account')} />
            <UserMenu
              fullName={session.firstName + ' ' + session.lastName}
              organizationName={organization?.name || ''}
              onLogout={logout}
              role={session.role}
            />
          </div>
        )
      }
      languageSelector={<DeclarantLanguageSelector />}
    >
      <div className="mt-8 space-y-8">
        <div className="space-y-8">
          <section className="h-1/4 flex flex-col justify-center w-full gap-2 pb-4">
            {declarants.length > 0 && (
              <DeclarantsMenu
                declarantName={declarantName || ''}
                declarants={declarantOptions || []}
              />
            )}
            <NavbarLink to="/cbam-reporting">{t('common:navbar.links.cbamReporting')}</NavbarLink>
          </section>
          <section className="flex-1 space-y-2 border-t border-gray-400 pt-4">
            <NavbarLink to="/cbam-simulations">
              {t('common:navbar.links.cbamSimulations')}
            </NavbarLink>
            <NavbarLink to="/suppliers">{t('common:navbar.links.suppliers')} </NavbarLink>
            {hasRepresentativeDeclarant ? (
              <NavbarLink to="/importers">{t('common:navbar.links.importers')}</NavbarLink>
            ) : (
              <Tooltip content={t('common:navbar.links.importersDisabledTooltip')} position="top">
                <NavbarLink to="/importers" disabled={true}>
                  {t('common:navbar.links.importers')}
                </NavbarLink>
              </Tooltip>
            )}
          </section>
        </div>
      </div>
    </AppSidebar>
  )
}
